import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getMe, getSurveyProgress, getSurveyPaper, getVerifyPaper } from '../api';

export const EnterpriseContext = createContext(null);
export const useEnterprise = () => useContext(EnterpriseContext);

export function EnterpriseProvider({ children }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const [enterprise, setEnterprise] = useState(null);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [surveyProgress, setSurveyProgress] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const [verifyRound, setVerifyRound] = useState(null);
  const [surveyPaper, setSurveyPaper] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState(null);
  const [verifyPaper, setVerifyPaper] = useState(null);

  useEffect(() => {
    getMe()
      .then((res) => setEnterprise(res.result))
      .catch(() => {})
      .finally(() => setIsAuthChecked(true));
  }, []);

  const refreshProgress = useCallback(async () => {
    if (!enterprise) return;
    try {
      const [progressRes, paperRes] = await Promise.all([
        getSurveyProgress(),
        getSurveyPaper(locale),
      ]);
      const sp = progressRes.result?.surveyProgress ?? 0;
      const vs = progressRes.result?.verifyStatus ?? 0;
      const vr = progressRes.result?.verifyRound ?? 0;
      const sa = progressRes.result?.answers || [];
      const paper = paperRes.result || null;

      setSurveyProgress(sp);
      setValidationStatus(vs);
      setVerifyRound(vr);
      setSavedAnswers(sa);
      setSurveyPaper(paper);

      let vp = null;
      if (vs >= 10) {
        try {
          const verifyRes = await getVerifyPaper(locale);
          vp = verifyRes.result || null;
        } catch {}
      }
      setVerifyPaper(vp);
    } catch {
      setSurveyProgress(0);
      setValidationStatus(0);
      setVerifyRound(0);
    }
  }, [enterprise, locale]);

  const enterpriseId = enterprise?.id;

  useEffect(() => {
    if (!enterpriseId) return;
    refreshProgress();
  }, [enterpriseId, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const requireAuth = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return (
    <EnterpriseContext.Provider
      value={{
        enterprise,
        isAuthChecked,
        surveyProgress,
        validationStatus,
        verifyRound,
        surveyPaper,
        savedAnswers,
        verifyPaper,
        setEnterprise,
        setValidationStatus,
        refreshProgress,
        requireAuth,
      }}
    >
      {children}
    </EnterpriseContext.Provider>
  );
}
